/**
 * 筛选条件定义与判定。
 *
 * 一列的筛选状态形如：
 * {
 *   // 值筛选：勾选列表。null/undefined 表示"全选"（不过滤）
 *   selectedValues: ['A', 'B'] | null,
 *   // 条件筛选：与值筛选是"与"关系，null 表示不启用
 *   condition: { name: 'contains', args: ['abc'] } | null
 * }
 */

/** 空值的统一展示文本（与 UI 中的"(空白)"对应） */
export const BLANK_LABEL = '(空白)';

/**
 * 把单元格原始值归一化成用于比较/展示的字符串。
 * null/undefined/'' 统一视为空值。
 *
 * @param {*} value 单元格值
 * @returns {String}
 */
export function normalizeValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(value);
}

/**
 * 值转为可比较的数字，无法转换时返回 null。
 *
 * @param {*} value
 * @returns {Number|null}
 */
function toNumber(value) {
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  const text = normalizeValue(value).trim();

  if (text === '') {
    return null;
  }

  // 去掉千分位分隔符，兼容 numeric 列格式化后的文本
  const numeric = Number(text.replace(/,/g, ''));

  return isNaN(numeric) ? null : numeric;
}

/**
 * 条件筛选算子表。
 * 每项签名为 (cellValue, args) => Boolean。
 */
export const CONDITIONS = {
  none: () => true,

  empty: value => normalizeValue(value) === '',

  not_empty: value => normalizeValue(value) !== '',

  eq: (value, args) => normalizeValue(value).toLowerCase() === normalizeValue(args[0]).toLowerCase(),

  neq: (value, args) => normalizeValue(value).toLowerCase() !== normalizeValue(args[0]).toLowerCase(),

  contains: (value, args) => normalizeValue(value).toLowerCase()
    .indexOf(normalizeValue(args[0]).toLowerCase()) !== -1,

  not_contains: (value, args) => normalizeValue(value).toLowerCase()
    .indexOf(normalizeValue(args[0]).toLowerCase()) === -1,

  begins_with: (value, args) => normalizeValue(value).toLowerCase()
    .indexOf(normalizeValue(args[0]).toLowerCase()) === 0,

  ends_with: (value, args) => {
    const text = normalizeValue(value).toLowerCase();
    const needle = normalizeValue(args[0]).toLowerCase();

    return needle === '' ? true : text.lastIndexOf(needle) === text.length - needle.length;
  },

  gt: (value, args) => {
    const a = toNumber(value);
    const b = toNumber(args[0]);

    return a === null || b === null ? false : a > b;
  },

  gte: (value, args) => {
    const a = toNumber(value);
    const b = toNumber(args[0]);

    return a === null || b === null ? false : a >= b;
  },

  lt: (value, args) => {
    const a = toNumber(value);
    const b = toNumber(args[0]);

    return a === null || b === null ? false : a < b;
  },

  lte: (value, args) => {
    const a = toNumber(value);
    const b = toNumber(args[0]);

    return a === null || b === null ? false : a <= b;
  },

  between: (value, args) => {
    const target = toNumber(value);
    const min = toNumber(args[0]);
    const max = toNumber(args[1]);

    if (target === null || min === null || max === null) {
      return false;
    }

    return target >= Math.min(min, max) && target <= Math.max(min, max);
  },
};

/**
 * 条件下拉的可选项（供 UI 渲染）。
 */
export const CONDITION_OPTIONS = [
  { name: 'none', label: '无', argCount: 0 },
  { name: 'empty', label: '为空', argCount: 0 },
  { name: 'not_empty', label: '不为空', argCount: 0 },
  { name: 'eq', label: '等于', argCount: 1 },
  { name: 'neq', label: '不等于', argCount: 1 },
  { name: 'contains', label: '包含', argCount: 1 },
  { name: 'not_contains', label: '不包含', argCount: 1 },
  { name: 'begins_with', label: '开头是', argCount: 1 },
  { name: 'ends_with', label: '结尾是', argCount: 1 },
  { name: 'gt', label: '大于', argCount: 1 },
  { name: 'gte', label: '大于或等于', argCount: 1 },
  { name: 'lt', label: '小于', argCount: 1 },
  { name: 'lte', label: '小于或等于', argCount: 1 },
  { name: 'between', label: '介于', argCount: 2 },
];

/**
 * 判断某个单元格值是否通过该列的筛选状态。
 *
 * @param {*} value 单元格值
 * @param {Object} state 该列的筛选状态
 * @returns {Boolean}
 */
export function matchState(value, state) {
  if (!state) {
    return true;
  }

  const { selectedValues, condition } = state;

  // 值筛选：selectedValues 为 null 表示全选
  if (Array.isArray(selectedValues)) {
    if (selectedValues.indexOf(normalizeValue(value)) === -1) {
      return false;
    }
  }

  // 条件筛选
  if (condition && condition.name && condition.name !== 'none') {
    const predicate = CONDITIONS[condition.name];

    if (predicate && !predicate(value, condition.args || [])) {
      return false;
    }
  }

  return true;
}

/**
 * 该列筛选状态是否处于"生效中"（用于表头高亮）。
 *
 * @param {Object} state
 * @param {Number} totalDistinctCount 该列去重值总数
 * @returns {Boolean}
 */
export function isStateActive(state, totalDistinctCount) {
  if (!state) {
    return false;
  }

  if (state.condition && state.condition.name && state.condition.name !== 'none') {
    return true;
  }

  if (Array.isArray(state.selectedValues)) {
    // 全选等同于未筛选
    return state.selectedValues.length !== totalDistinctCount;
  }

  return false;
}
